import api from "@/plugins/api";
import { isStubMode } from "./stub-mode";
import { appendQuery } from "@/shared/utils/url";
import { DocumentKind, DocumentStatus, DocumentType } from "../../domain/types/document";
const BASE_PATH = "letters";
/**
 * In-memory letters used while no BFF is configured, mirroring the documents
 * stub. Letters are personal, so the stub behaves as if a single user owned
 * every row.
 */
const MOCK_LETTERS = [];
function delay(value, ms = 200) {
    return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}
/**
 * API client for free-form letters. Every call is scoped upstream to the
 * caller's own letters — there is no owner parameter on the wire.
 */
export const lettersApi = {
    async getLetters(params = {}) {
        if (isStubMode()) {
            const term = params.search?.trim().toLowerCase();
            const filtered = MOCK_LETTERS.filter((letter) => !term || letter.name.toLowerCase().includes(term));
            if (params.sortBy === "name") {
                filtered.sort((a, b) => params.sortDirection === "asc"
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name));
            }
            const page = params.page ?? 1;
            const pageSize = params.pageSize ?? 20;
            const start = (page - 1) * pageSize;
            return delay({
                items: filtered.slice(start, start + pageSize),
                page,
                pageSize,
                totalCount: filtered.length,
                totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
            });
        }
        // Wire names must match the BFF's [FromQuery] parameters exactly (R61).
        const url = appendQuery(BASE_PATH, {
            page: params.page,
            pageSize: params.pageSize,
            searchTerm: params.search?.trim() || undefined,
            sortBy: params.sortBy,
            sortDirection: params.sortDirection,
        });
        const response = await api.get(url, { unwrapEnvelope: true });
        return response.data;
    },
    async getLetter(id) {
        if (isStubMode()) {
            const letter = MOCK_LETTERS.find((l) => l.id === id);
            if (!letter) {
                throw new Error(`Letter ${id} not found`);
            }
            return delay({ ...letter, fileType: "docx" });
        }
        const response = await api.get(`${BASE_PATH}/${id}`, { unwrapEnvelope: true });
        return response.data;
    },
    /**
     * Creates a letter from a blank Word document. The classification decides
     * which merge fields the editor offers, so it travels with the name.
     */
    async createLetter(body) {
        if (isStubMode()) {
            const stub = {
                id: `letter-stub-${Date.now()}`,
                name: body.name.trim(),
                type: DocumentType.Word,
                kind: DocumentKind.Letter,
                status: DocumentStatus.Draft,
                contextKey: body.contextKey,
                documentTypeKey: body.documentTypeKey,
                contentVersion: 1,
                updatedAt: new Date().toISOString(),
                fileType: "docx",
            };
            MOCK_LETTERS.unshift({ ...stub });
            return delay(stub);
        }
        const response = await api.post(BASE_PATH, body, { unwrapEnvelope: true });
        return response.data;
    },
    async getLetterEditorConfig(id) {
        if (isStubMode()) {
            const letter = await this.getLetter(id);
            return delay({
                document: {
                    fileType: "docx",
                    key: `${letter.id}-${Date.now()}`,
                    title: letter.name,
                    url: `https://stub.local/letters/${letter.id}.docx`,
                    permissions: { edit: true, download: true, print: true },
                },
                documentType: "word",
                editorConfig: {
                    mode: "edit",
                    lang: "fr",
                    user: { id: "stub-user", name: "Local Developer" },
                    callbackUrl: `https://stub.local/api/letters/${letter.id}/callback`,
                },
                token: "stub.jwt.token",
            });
        }
        const response = await api.get(`${BASE_PATH}/${id}/editor-config`, {
            unwrapEnvelope: true,
        });
        return response.data;
    },
    /**
     * Renames a letter. The classification is fixed at creation, so the name is
     * the only mutable metadata.
     */
    async renameLetter(id, name) {
        if (isStubMode()) {
            const idx = MOCK_LETTERS.findIndex((l) => l.id === id);
            if (idx < 0)
                throw new Error(`Letter ${id} not found`);
            MOCK_LETTERS[idx].name = name;
            return delay({ ...MOCK_LETTERS[idx], fileType: "docx" });
        }
        const response = await api.patch(`${BASE_PATH}/${id}`, { name }, { unwrapEnvelope: true });
        return response.data;
    },
    async deleteLetter(id) {
        if (isStubMode()) {
            const idx = MOCK_LETTERS.findIndex((l) => l.id === id);
            if (idx >= 0)
                MOCK_LETTERS.splice(idx, 1);
            await delay(undefined);
            return;
        }
        await api.delete(`${BASE_PATH}/${id}`, { unwrapEnvelope: true });
    },
};
